import { useMutation, useQuery } from '@apollo/client'
import { BodyText, Button, Col, HeadingText, Row } from '@paypalcorp/pp-react'
import { navigate } from '@reach/router'
import { merge } from 'lodash-es'
import SpinnerAdapter from 'ppbl/components/SpinnerAdapter'
import InfoText from 'ppbl/components/InfoText'
import { updateApplicationCachedData } from 'ppbl/graphql/__utils__'
import {
  submitApplicationMutation,
  updateApplicationMutation,
} from 'ppbl/graphql/mutations'
import { getCreditApplicationDataQuery } from 'ppbl/graphql/queries'
import { useApplicationContext } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import serverData from 'ppbl/utils/server-data'
import { trackClick, trackPage } from 'ppbl/utils/fpti-helper'
import { FPTI_EVENTS } from 'ppbl/utils/fptiEventNames'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { santizeInputFields } from 'ppbl/utils/string-helper'
import { scrollOnErrors } from 'ppbl/utils/validation-helper'
import React, { useEffect, useRef } from 'react'
import { Field, Form } from 'react-final-form'
import {
  scrollToTopOnContinue,
  submitApplicationPayloadGenerator,
  updateApplicationPayloadGeneratorPage5,
  handleGraphqlError,
} from 'ppbl/screens/__utils__'
import { applicationPage5Fields } from '../applicationFieldMapper'
import {
  electronicCommunications,
  writtenInstructions,
} from '../applicationFields'
import { cloneAndMask } from 'ppbl/utils/data-masker'
import './styles.css'
import createDecorator from 'ppbl/utils/form/decorator'
import PreApprovalBanner from 'ppbl/components/PreApprovalBanner'

type Props = {
  applicationType: string
  creditApplicationLoading: boolean
}

const fptiData = {
  page: 'applicationPage5',
  event_name: FPTI_EVENTS.PAGE.PAGE5,
}

const ApplicationPage5: React.FC<Props> = ({
  applicationType,
  creditApplicationLoading,
}) => {
  const { data, updateApplicationContext } = useApplicationContext()

  useEffect(() => {
    trackPage(
      merge(fptiData, {
        applicationId: data?.applicationMetaData?.applicationId,
      }),
    )
    clientLogger.info(
      'Application Page 5 is loaded - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
  }, [])

  const isPaypalApplication = applicationType === 'paypal'

  const renewalToken = serverData.startParams?.renewalToken

  const { data: cachedApplicationData } = useQuery(
    getCreditApplicationDataQuery,
  )

  const [submitApplication, { loading: submitLoading }] = useMutation(
    submitApplicationMutation,
    {
      onCompleted(submitResponse) {
        clientLogger.info(
          'Submit Application page 5 response ',
          cloneAndMask(submitResponse),
        )

        // push the response.data.credit.businessCredit.createApplication.creditApplication into context
        sessionStorage.setItem('mostRecentCompletedPage', 'applicationPage5')
        updateApplicationContext({
          applicationMetaData: {
            ...data.applicationMetaData,
            mostRecentCompletedPage: 'applicationPage5',
          },
        })
        navigate(
          `/merchant-lending/loanbuilder/application/us/${applicationType}/loading`,
        )
      },
      onError(submitError) {
        handleGraphqlError(
          submitError,
          'Error while calling GraphQl submit end point on application page 5, redirecting to error page',
          'ApplicationPage 5 - submitApplicationMutation',
          data?.applicationMetaData?.applicationId,
          renewalToken,
          data?.applicationMetaData?.legalBusinessName,
          applicationType,
        )
      },
    },
  )

  const [updateApplication, { loading }] = useMutation(
    updateApplicationMutation,
    {
      onCompleted(response) {
        clientLogger.info(
          'Response received after verifying identity and ownership',
          cloneAndMask(response),
        )

        submitApplication({
          variables: {
            submitInput: submitApplicationPayloadGenerator(
              data.applicationMetaData.applicationId,
            ),
          },
        })
      },
      onError(updateApplicationError) {
        handleGraphqlError(
          updateApplicationError,
          'Error encountered while verifying identity and ownership on application page 5, redirecting to error page',
          'ApplicationPage 5 - updateApplicationMutation',
          data?.applicationMetaData?.applicationId,
          renewalToken,
          data?.applicationMetaData?.legalBusinessName,
          applicationType,
        )
      },
    },
  )

  const handleApplicationSubmit = async (values: any) => {
    trackClick(
      merge(fptiData, {
        component: 'applicationPage5_continue_button',
        link: 'applicationPage5_continue_button',
        applicationId: data?.applicationMetaData?.applicationId,
        event_name: FPTI_EVENTS.CLICK.PAGE_CONTINUE.PAGE5,
      }),
    )

    scrollToTopOnContinue()

    clientLogger.info(
      'application page 5 submitted - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    clientLogger.info(
      'Legal bussiness name for the application',
      data?.applicationMetaData?.legalBusinessName,
    )
    if (isPaypalApplication) {
      clientLogger.info(
        'paypal users encrypted account number',
        serverData?.user?.encryptedAccountNumber,
      )
    } else if (renewalToken) {
      clientLogger.info('renewal user renewal token', renewalToken)
    }

    //sanitize the input values to prevent XSS
    santizeInputFields(values)

    updateApplicationCachedData(values, cachedApplicationData)
    updateApplication({
      variables: {
        updateInput: updateApplicationPayloadGeneratorPage5(
          values,
          cachedApplicationData.legalBusinessName,
          data.applicationMetaData.applicationId,
        ),
      },
    })
  }

  const focusOnErrors = createDecorator()

  return (
    <>
      {loading || submitLoading || creditApplicationLoading ? (
        <div className="spinner">
          <SpinnerAdapter />
        </div>
      ) : (
        <>
          {data?.eligibilityData?.preApproved && (
            <PreApprovalBanner
              approvalCategory={data?.eligibilityData?.category}
              expiryDate={data?.eligibilityData?.expiryDate}
            />
          )}
        <Form onSubmit={handleApplicationSubmit} decorators={[focusOnErrors]}>
          {({ handleSubmit, hasValidationErrors }) => (
            <div className="applicationFormContainer">
              <form onSubmit={handleSubmit}>
                <div className="applicationTextSection">
                  <Row>
                    <Col form="full">
                      <HeadingText size="sm">
                        {getLocalizedText(
                          'application.applicationPages.applicationPage5.headingText',
                        )}
                      </HeadingText>
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <BodyText>
                        {getLocalizedText(
                          'application.applicationPages.applicationPage5.checkEligibilityText',
                        )}
                      </BodyText>
                    </Col>
                  </Row>
                </div>
                <div className="applicationFields">
                  <Row>
                    <Col form="full">
                      <BodyText strong data-dd-privacy="mask">
                        {cachedApplicationData?.legalBusinessName}
                      </BodyText>
                    </Col>
                  </Row>
                  {applicationPage5Fields.map((applicationField) => {
                    const { name, ...props } = applicationField
                    return (
                      <Row key={name}>
                        <Col form="full">
                          <Field
                            name={name}
                            {...props}
                            initialValue={cachedApplicationData?.[name] || null}
                          />
                        </Col>
                      </Row>
                    )
                  })}
                  <div>
                    <Row>
                      <Col className="checkboxGroup" form="full">
                        <Field
                          name={electronicCommunications?.name}
                          {...electronicCommunications}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="checkboxGroup" form="full">
                        <Field
                          name={writtenInstructions?.name}
                          {...writtenInstructions}
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
                <Button
                  className="ctaSection"
                  size="lg"
                  onClick={() => scrollOnErrors(hasValidationErrors)}
                  type="submit"
                >
                  {getLocalizedText(
                    'application.applicationPages.applicationPage5.continueButtonText',
                  )}
                </Button>
              </form>
              <InfoText />
            </div>
          )}
        </Form>
        </>
      )}
    </>
  )
}

export default ApplicationPage5
